import React, { useMemo } from 'react';
import { Card, ProgressBar, Badge, Row, Col, Button } from 'react-bootstrap';

const LearningPath = ({ modules = [], progress = {}, loading = false, moduleLinkBase = '/learner/module' }) => {
  const progressMap = progress || {};

  const moduleStatus = useMemo(() => {
    const status = {};
    const enrolled = new Set();
    const completed = new Set();

    Object.entries(progressMap).forEach(([moduleId, moduleProgress]) => {
      if (moduleProgress?.progress >= 100) {
        completed.add(moduleId);
      } else if (moduleProgress) {
        enrolled.add(moduleId);
      }
    });

    modules.forEach((module) => {
      if (completed.has(module.id)) {
        status[module.id] = 'completed';
      } else if (enrolled.has(module.id)) {
        status[module.id] = 'enrolled';
      } else {
        status[module.id] = 'available';
      }
    });

    return status;
  }, [modules, progressMap]);

  const getModuleStatus = (moduleId) => moduleStatus[moduleId] || 'available';

  const getModuleProgress = (moduleId) => {
    const moduleProgress = progressMap?.[moduleId];
    if (!moduleProgress) {
      return 0;
    }
    return Math.round(moduleProgress.progress || 0);
  };

  const getModuleAchievements = (moduleId) => {
    const moduleProgress = progressMap?.[moduleId];
    if (!moduleProgress || !Array.isArray(moduleProgress.achievements)) {
      return 0;
    }
    return moduleProgress.achievements.length;
  };

  const getModuleTimeSpent = (moduleId) => {
    const moduleProgress = progressMap?.[moduleId];
    if (!moduleProgress) {
      return 0;
    }
    return moduleProgress.totalTimeSpent || moduleProgress.timeSpent || 0;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getModuleLink = (moduleId) => `${moduleLinkBase}/${moduleId}`;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'success';
      case 'Intermediate':
        return 'warning';
      case 'Advanced':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'enrolled':
        return <Badge bg="primary">In Progress</Badge>;
      case 'available':
        return <Badge bg="secondary">Assigned</Badge>;
      default:
        return <Badge bg="dark">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Loading learning path...
      </div>
    );
  }

  if (!modules || modules.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p className="mb-0">No modules available yet. Please check back soon.</p>
      </div>
    );
  }

  return (
    <div className="learning-path">
      <h3>Learning Path</h3>
      <Row>
        {modules.map((module) => {
          const status = getModuleStatus(module.id);
          const progressPercentage = getModuleProgress(module.id);
          const achievementsCount = getModuleAchievements(module.id);
          const timeSpent = getModuleTimeSpent(module.id);
          const isLocked = status === 'locked';

          return (
            <Col md={6} lg={4} key={module.id} className="mb-4">
              <Card className={`h-100 ${isLocked ? 'opacity-50' : ''}`}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="card-title">{module.moduleName || module.title}</h5>
                    {getStatusBadge(status)}
                  </div>

                  <p className="text-muted small">
                    {module.description ? module.description.substring(0, 120) : 'No description provided.'}
                    {module.description && module.description.length > 120 ? '…' : ''}
                  </p>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="text-muted">Progress</small>
                      <small className="text-muted">{progressPercentage}%</small>
                    </div>
                    <ProgressBar
                      now={progressPercentage}
                      variant={status === 'completed' ? 'success' : 'primary'}
                    />
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Badge bg={getDifficultyColor(module.difficulty || 'Unknown')}>
                      {module.difficulty || 'Unknown'}
                    </Badge>
                    <small className="text-muted">
                      {module.estimatedHours ? `${module.estimatedHours}h` : formatTime(timeSpent)}
                    </small>
                  </div>

                  {Array.isArray(module.prerequisites) && module.prerequisites.length > 0 && (
                    <div className="mb-3">
                      <small className="text-muted">Prerequisites: </small>
                      {module.prerequisites.map((prereq) => (
                        <Badge key={prereq} bg="light" text="dark" className="me-1">
                          {prereq}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {achievementsCount} achievement{achievementsCount === 1 ? '' : 's'}
                    </small>

                    {status === 'available' && (
                      <Button
                        size="sm"
                        variant="primary"
                        href={getModuleLink(module.id)}
                      >
                        View
                      </Button>
                    )}

                    {status === 'enrolled' && (
                      <Button
                        size="sm"
                        variant="success"
                        href={getModuleLink(module.id)}
                      >
                        Continue
                      </Button>
                    )}

                    {status === 'completed' && (
                      <Button
                        size="sm"
                        variant="outline-success"
                        href={getModuleLink(module.id)}
                      >
                        Review
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default LearningPath;
